import { getTeamsAuthToken } from '../auth/teamsContext';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly title: string,
    public readonly detail?: string
  ) {
    super(`${status} ${title}${detail ? ' — ' + detail : ''}`);
  }
}

async function parseProblem(response: Response): Promise<ApiError> {
  try {
    const body = (await response.clone().json()) as { title?: string; detail?: string };
    return new ApiError(response.status, body.title ?? response.statusText, body.detail);
  } catch {
    return new ApiError(response.status, response.statusText);
  }
}

async function handle<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw await parseProblem(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

function qs(params?: Record<string, string | number | undefined>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  const search = new URLSearchParams();
  for (const [k, v] of entries) {
    search.set(k, String(v));
  }
  return `?${search.toString()}`;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getTeamsAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const http = {
  async get<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> {
    const auth = await authHeaders();
    return fetch(`/api${path}${qs(params)}`, {
      credentials: 'include',
      headers: { ...auth },
    }).then(handle<T>);
  },
  async post<T>(path: string, body?: unknown): Promise<T> {
    const auth = await authHeaders();
    return fetch(`/api${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...auth },
      body: body == null ? undefined : JSON.stringify(body),
    }).then(handle<T>);
  },
  async put<T>(path: string, body: unknown): Promise<T> {
    const auth = await authHeaders();
    return fetch(`/api${path}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...auth },
      body: JSON.stringify(body),
    }).then(handle<T>);
  },
  async del<T = void>(path: string): Promise<T> {
    const auth = await authHeaders();
    return fetch(`/api${path}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { ...auth },
    }).then(handle<T>);
  },
};
